import type { AdminCapability, Permission, PortalFeature } from "$lib/api/contract";

// Hand-rolled History-API router (~150 lines). Owning it keeps route guards
// first-class (session/org/capability), gives deterministic abort-on-navigation, and
// lets us scrub sensitive query params — worth avoiding a third-party dependency in a
// security-sensitive console. See FRONTEND_PLAN §1.

export type RouteName =
  | "login"
  | "login.continue"
  | "login.code"
  | "verify.email"
  | "auth.callback"
  | "auth.error"
  | "invite.accept"
  | "claim"
  | "upgrade.complete"
  | "signed-out"
  | "extension.login"
  | "welcome"
  | "root"
  | "admin.entitlements"
  | "admin.revenue"
  | "admin.audit"
  | "admin.discord"
  | "admin.customer"
  | "admin.customer.quote.new"
  | "admin.contracts"
  | "admin.contract.new"
  | "admin.contract"
  | "admin.contract-queue"
  | "admin.contract-queue.item"
  | "admin.organizations"
  | "overview"
  | "org.users"
  | "org.users.member"
  | "plan.seats"
  | "access.policies"
  | "extension.policy"
  | "unlicensed"
  | "people.legacy"
  | "people.member.legacy"
  | "billing"
  | "org.quotes"
  | "org.quote"
  | "sso"
  | "audit"
  | "settings"
  | "notfound";

export interface RouteGuard {
  session?: boolean;
  org?: boolean;
  administrator?: boolean;
  /** Extra support capability on top of `administrator` (e.g. organization recovery). */
  capability?: AdminCapability;
  feature?: PortalFeature;
  /** Capability denial renders IN-PLACE (never redirects). */
  permission?: Permission;
  /** Permanent client-side redirect target for a superseded path (`:param` allowed). */
  redirectTo?: string;
}

interface RouteDef {
  name: RouteName;
  pattern: string;
  keys: string[];
  regex: RegExp;
  guard: RouteGuard;
  scope: "public" | "app" | "admin";
}

function compile(pattern: string): { keys: string[]; regex: RegExp } {
  const keys: string[] = [];
  const source = pattern
    .replace(/\/$/, "")
    .replace(/:[A-Za-z0-9_]+/g, (match) => {
      keys.push(match.slice(1));
      return "([^/]+)";
    });
  return { keys, regex: new RegExp(`^${source || "/"}$`) };
}

const ROUTES: Array<{ name: RouteName; pattern: string; guard: RouteGuard; scope: "public" | "app" | "admin" }> = [
  { name: "login", pattern: "/login", guard: {}, scope: "public" },
  { name: "login.continue", pattern: "/login/continue", guard: {}, scope: "public" },
  { name: "login.code", pattern: "/login/code", guard: {}, scope: "public" },
  { name: "verify.email", pattern: "/verify-email", guard: {}, scope: "public" },
  { name: "auth.callback", pattern: "/auth/callback", guard: {}, scope: "public" },
  { name: "auth.error", pattern: "/auth/error", guard: {}, scope: "public" },
  { name: "invite.accept", pattern: "/invite/accept", guard: {}, scope: "public" },
  { name: "claim", pattern: "/claim", guard: {}, scope: "public" },
  // The post-Stripe landing page. Deliberately session-free: the buyer may pay in a
  // browser that was never signed in, and the opaque `claim` in the query is the only
  // authorization the status read needs.
  { name: "upgrade.complete", pattern: "/upgrade/complete", guard: {}, scope: "public" },
  { name: "signed-out", pattern: "/signed-out", guard: {}, scope: "public" },
  { name: "extension.login", pattern: "/extension-login", guard: { session: true }, scope: "public" },
  { name: "welcome", pattern: "/welcome", guard: { session: true }, scope: "public" },
  { name: "root", pattern: "/", guard: { session: true }, scope: "public" },
  { name: "admin.entitlements", pattern: "/admin/entitlements", guard: { session: true, administrator: true }, scope: "admin" },
  { name: "admin.revenue", pattern: "/admin/revenue", guard: { session: true, administrator: true }, scope: "admin" },
  { name: "admin.audit", pattern: "/admin/audit", guard: { session: true, administrator: true }, scope: "admin" },
  // Discord operations are a server-owned capability: present only where Builders
  // invitations are enabled, so a disabled environment never renders the console.
  { name: "admin.discord", pattern: "/admin/discord", guard: { session: true, administrator: true, capability: "discord.manage" }, scope: "admin" },
  // The full-page quote composer, declared before the `:customerId` detail pattern so
  // the more specific path is matched first regardless of segment counting. Quoting
  // materializes a Stripe subscription on acceptance, so it needs `invoices.manage`.
  { name: "admin.customer.quote.new", pattern: "/admin/entitlements/:customerId/quotes/new", guard: { session: true, administrator: true, capability: "invoices.manage" }, scope: "admin" },
  // Deep-linkable customer detail. Declared after the list so the exact list path
  // keeps winning, and it loads its own customer so a cold reload works.
  { name: "admin.customer", pattern: "/admin/entitlements/:customerId", guard: { session: true, administrator: true }, scope: "admin" },
  // Contract library and signing queue. Contracts are issued onto quotes and their
  // final countersignature raises the invoice, so both surfaces sit behind the same
  // `invoices.manage` capability as the composer. The literal `/new` path is declared
  // before the `:contractId` pattern so it is never read as an id.
  { name: "admin.contracts", pattern: "/admin/contracts", guard: { session: true, administrator: true, capability: "invoices.manage" }, scope: "admin" },
  { name: "admin.contract.new", pattern: "/admin/contracts/new", guard: { session: true, administrator: true, capability: "invoices.manage" }, scope: "admin" },
  { name: "admin.contract", pattern: "/admin/contracts/:contractId", guard: { session: true, administrator: true, capability: "invoices.manage" }, scope: "admin" },
  { name: "admin.contract-queue", pattern: "/admin/contract-queue", guard: { session: true, administrator: true, capability: "invoices.manage" }, scope: "admin" },
  { name: "admin.contract-queue.item", pattern: "/admin/contract-queue/:contractId", guard: { session: true, administrator: true, capability: "invoices.manage" }, scope: "admin" },
  // Organization recovery is a separate support capability, not implied by `administrator`.
  { name: "admin.organizations", pattern: "/admin/organizations", guard: { session: true, administrator: true, capability: "organizations.support" }, scope: "admin" },
  { name: "overview", pattern: "/orgs/:orgId/overview", guard: { session: true, org: true, feature: "portal.overview" }, scope: "app" },
  // Organization Users supersedes the old People surface; the previous paths keep
  // working so existing links, bookmarks, and emailed deep links don't break.
  { name: "org.users", pattern: "/orgs/:orgId/users", guard: { session: true, org: true, feature: "portal.people", permission: "members.read" }, scope: "app" },
  { name: "people.legacy", pattern: "/orgs/:orgId/people", guard: { redirectTo: "/orgs/:orgId/users" }, scope: "app" },
  { name: "plan.seats", pattern: "/orgs/:orgId/plan", guard: { session: true, org: true, feature: "portal.overview", permission: "members.read" }, scope: "app" },
  { name: "access.policies", pattern: "/orgs/:orgId/access-policies", guard: { session: true, org: true, feature: "portal.access_policies", permission: "security.manage" }, scope: "app" },
  { name: "extension.policy", pattern: "/orgs/:orgId/extension-policy", guard: { session: true, org: true, feature: "portal.extension_policy", permission: "security.manage" }, scope: "app" },
  // The unseated-member dead end. Guarded by its own feature so a seated member can
  // never land here and an unseated one has somewhere valid to be.
  { name: "unlicensed", pattern: "/orgs/:orgId/unlicensed", guard: { session: true, org: true, feature: "portal.unlicensed" }, scope: "app" },
  { name: "billing", pattern: "/orgs/:orgId/billing", guard: { session: true, org: true, feature: "portal.billing", permission: "billing.manage" }, scope: "app" },
  // Quote surfaces are recipient-authorized SERVER-side (the signed-in member's email
  // must be on the quote), so they deliberately carry no `billing.manage` permission
  // guard — a procurement contact who is not an owner still has to reach their quote.
  // The list is declared before the deep link so the exact path keeps winning.
  { name: "org.quotes", pattern: "/orgs/:orgId/quotes", guard: { session: true, org: true, feature: "portal.billing" }, scope: "app" },
  { name: "org.quote", pattern: "/orgs/:orgId/quotes/:quoteId", guard: { session: true, org: true, feature: "portal.billing" }, scope: "app" },
  { name: "org.users.member", pattern: "/orgs/:orgId/users/:userId", guard: { session: true, org: true, feature: "portal.people", permission: "members.read" }, scope: "app" },
  { name: "people.member.legacy", pattern: "/orgs/:orgId/people/:userId", guard: { redirectTo: "/orgs/:orgId/users/:userId" }, scope: "app" },
  { name: "sso", pattern: "/orgs/:orgId/sso", guard: { session: true, org: true, feature: "portal.sso", permission: "sso.manage" }, scope: "app" },
  { name: "audit", pattern: "/orgs/:orgId/audit", guard: { session: true, org: true, feature: "portal.audit", permission: "audit.read" }, scope: "app" },
  { name: "settings", pattern: "/orgs/:orgId/settings", guard: { session: true, org: true, feature: "portal.settings" }, scope: "app" }
];

const TABLE: RouteDef[] = ROUTES.map((route) => {
  const { keys, regex } = compile(route.pattern);
  return { ...route, keys, regex };
});

const NOT_FOUND: RouteDef = { name: "notfound", pattern: "*", keys: [], regex: /.^/, guard: {}, scope: "public" };

export interface RouteMatch {
  name: RouteName;
  def: RouteDef;
  params: Record<string, string>;
  query: URLSearchParams;
  path: string;
}

function match(path: string, search: string): RouteMatch {
  const clean = path.length > 1 ? path.replace(/\/$/, "") : path;
  for (const def of TABLE) {
    const result = def.regex.exec(clean);
    if (result) {
      const params: Record<string, string> = {};
      def.keys.forEach((key, index) => {
        params[key] = decodeURIComponent(result[index + 1] ?? "");
      });
      return { name: def.name, def, params, query: new URLSearchParams(search), path: clean };
    }
  }
  return { name: "notfound", def: NOT_FOUND, params: {}, query: new URLSearchParams(search), path: clean };
}

function current(): RouteMatch {
  return match(window.location.pathname, window.location.search);
}

class Router {
  match = $state<RouteMatch>(match("/", ""));

  init(): void {
    this.match = current();
    window.addEventListener("popstate", () => {
      this.match = current();
    });
  }

  navigate(to: string, options: { replace?: boolean } = {}): void {
    const url = new URL(to, window.location.origin);
    const target = url.pathname + url.search + url.hash;
    if (options.replace) window.history.replaceState({}, "", target);
    else window.history.pushState({}, "", target);
    this.match = match(url.pathname, url.search);
  }

  /** Remove sensitive params from the URL without a navigation (landing cleanUrl). */
  scrubQuery(keys: string[]): void {
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of keys) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (changed) {
      window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
      this.match = match(url.pathname, url.search);
    }
  }

  /** Reflect filter state to the URL query without pushing history. */
  replaceQuery(params: Record<string, string | null | undefined>): void {
    const url = new URL(window.location.href);
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === "") url.searchParams.delete(key);
      else url.searchParams.set(key, value);
    }
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    // Filters change in place. Replacing the route also reruns navigation effects
    // (including heading focus), interrupting typing after each debounced search.
    this.match.query = new URLSearchParams(url.search);
  }
}

export const router = new Router();

interface LinkOptions {
  beforeNavigate?: ((event: MouseEvent) => void) | undefined;
}

/** Svelte action: intercept same-origin anchor clicks and route them client-side. */
export function link(node: HTMLAnchorElement, options: LinkOptions = {}) {
  let current = options;
  function onClick(event: MouseEvent): void {
    current.beforeNavigate?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const href = node.getAttribute("href");
    if (!href || href.startsWith("http") || node.target === "_blank") return;
    event.preventDefault();
    router.navigate(href);
  }
  node.addEventListener("click", onClick);
  return {
    update(next: LinkOptions) {
      current = next;
    },
    destroy() {
      node.removeEventListener("click", onClick);
    }
  };
}
